<template>
    <highcharts class="hc" :ref="refId" :options="options" style="max-height: 500px"></highcharts>
</template>

<script>
    // utils
    import {getKey} from "../../utils/common";
    // constants
    import {chartColors} from "../../constants/chart-constants";
    // external
    import Highcharts from 'highcharts';
    import exportingInit from 'highcharts/modules/exporting';
    import exportingData from 'highcharts/modules/export-data';
    // mixins
    import computePlotLinesMixin from "../../mixins/computePlotLinesMixin";
    import ChartZoomControlMixin from "../../mixins/ChartZoomControlMixin";
    import ChartOptionsMixin from "../../mixins/ChartOptionsMixin";

    Highcharts.setOptions({
        colors: chartColors
    });

    exportingInit(Highcharts);
    exportingData(Highcharts);
    /**
     * Wrapper for highcharts component.
     * Has dynamic ref key and zoom reset handler.
     * Initializes localization for the chart and
     * holds the options for AREA type charts.
     */

    export default {
        name: "TimeSeriesChart",
        mixins: [computePlotLinesMixin, ChartZoomControlMixin, ChartOptionsMixin],
        props: {
            zoomKey: {
                type: Number,
                required: true
            },
            data: {
                type: Object,
                required: true
            },
            metric: {
                type: String
            }
        },
        created() {
            Highcharts.setOptions({
                lang: this.getChartTranslations()
            });
        },
        computed: {
            /**
             * Get a dynamic key for referencing the highcharts object.
             * NOTE: zoom level is controlled viz the zoomKey and this.
             */
            refId() {
                return getKey();
            },
            options() {
                let options = {
                    chart: {
                        zoomType: 'x',
                        type: 'area'
                    },
                    exporting: this.getChartExportingOptions(),
                    resetZoomButton: {
                        theme: {
                            display: 'none'
                        }
                    },
                    title: {
                        text: null
                    },
                    xAxis: {
                        type: 'datetime',
                        title: {
                            text: '',
                            style: {
                                color: "#6299EB"
                            }
                        },
                        startOnTick: true,
                        endOnTick: true,
                        min: this.data.startDate,
                        max: this.data.endDate
                    },
                    yAxis: {
                        title: {
                            // text: this.$i18n.t('sensors-page.y-label'),
                            text: this.$i18n.t(this.metric || 'sensors-page.y-label'),
                            style: {
                                color: "#6299EB"
                            }
                        },
                        labels: {
                            x: 0,
                            y: -2
                        }
                    },
                    legend: {
                        enabled: false
                    },
                    credits: {
                        enabled: false
                    },
                    plotOptions: {
                        area: {
                            fillColor: {
                                linearGradient: {
                                    x1: 0,
                                    y1: 0,
                                    x2: 0,
                                    y2: 1
                                },
                                stops: [
                                    [0, Highcharts.getOptions().colors[0]],
                                    [1, Highcharts.color(Highcharts.getOptions().colors[0]).setOpacity(0).get('rgba')]
                                ]
                            },
                            marker: {
                                radius: 2
                            },
                            lineWidth: 1,
                            states: {
                                hover: {
                                    lineWidth: 1
                                }
                            },
                            threshold: null
                        }
                    }
                };

                // add data
                options.series = this.data.series;
                // add possible plot lines
                if (this.showPlotLine) {
                    options.xAxis.plotLines = this.computePlotLines(this.data.plotLines.xAxis);
                    options.yAxis.plotLines = this.computePlotLines(this.data.plotLines.yAxis);
                }

                return options;
            }
        }
    };
</script>
